import {
  CalculationServiceEntryType,
  CalculationServiceType,
} from "@/services/backend/qpm/calculations/types/calculation-enum";
import { ServiceContainerResult } from "@/services/backend/qpm/calculations/types/calculation";
import { CalculationServiceEntryUtil } from "@/services/backend/qpm/calculations/lib/calculation-service-entry-util";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { Value } from "@/shared/components/domain/qpm/inputs/number-input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";

export type ServiceContainerResultComponentProps = {
  results: ServiceContainerResult[];
  serviceTypeKey: CalculationServiceType;
};

export function ServiceContainerResultComponent({
  results,
  serviceTypeKey,
}: ServiceContainerResultComponentProps) {
  // Define the custom order for CalculationServiceEntryType
  const customOrder = [
    CalculationServiceEntryType.CheckPoint,
    CalculationServiceEntryType.CheckPointVisual,
    CalculationServiceEntryType.CheckPointIoT,
    CalculationServiceEntryType.CheckPointSCB,
    CalculationServiceEntryType.ALoRa,
  ];

  if (serviceTypeKey === CalculationServiceType.Installation) {
    customOrder.push(CalculationServiceEntryType.FIV);
  }

  // Group results by serviceEntryType and sort each group by amountOfMeasures
  const resultEntryMap: Record<
    CalculationServiceEntryType,
    ServiceContainerResult[]
  > = (results || []).reduce(
    (acc, result) => {
      if (!acc[result.serviceEntryType]) {
        acc[result.serviceEntryType] = [];
      }
      acc[result.serviceEntryType].push(result);
      return acc;
    },
    {} as Record<CalculationServiceEntryType, ServiceContainerResult[]>,
  );

  // Sort results within each group by amountOfMeasures
  Object.keys(resultEntryMap).forEach((type) => {
    const entryType = type as CalculationServiceEntryType;
    resultEntryMap[entryType].sort(
      (a, b) => a.amountOfMeasures - b.amountOfMeasures,
    );
  });

  return (
    <Accordion type="multiple" defaultValue={customOrder}>
      {customOrder.map((type) => {
        const sortedEntries = resultEntryMap[type] || [];
        if (sortedEntries.length === 0) return null;

        return (
          <AccordionItem value={type} key={type}>
            <AccordionTrigger className="bg-secondary rounded-none border-t px-4 font-bold uppercase">
              {CalculationServiceEntryUtil.toLongString(type, serviceTypeKey)}
            </AccordionTrigger>
            <AccordionContent>
              <div className="px-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-left">
                        {t("Anzahl Servicepunkte")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("Maßnahmen")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("Preis pro Maßnahme")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("Gesamtpreis in Euro")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedEntries.map((result) => (
                      <ServiceResultRow key={result.id} resultEntry={result} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export type ServiceResultRowProps = {
  resultEntry: ServiceContainerResult;
};

export function ServiceResultRow({ resultEntry }: ServiceResultRowProps) {
  return (
    <TableRow>
      <TableCell className="text-left font-medium">
        <Value value={resultEntry.amountOfMonitors} fractions={0} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={resultEntry.amountOfMeasures} fractions={0} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={resultEntry.costPerMeasure} fractions={2} />
      </TableCell>
      <TableCell className="text-right font-medium">
        <Value value={resultEntry.totalCost} fractions={2} />
      </TableCell>
    </TableRow>
  );
}
